import React, { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquare } from '@fortawesome/free-solid-svg-icons'
import {
    PieChart as RechartPieChart,
    Pie,
    Cell,
    Legend,
    ResponsiveContainer,
    Tooltip,
    Sector,
} from 'recharts'
import { LoadingIndicator } from 'components'
import idDirectory from './idAttributes'
import { clientTheme } from 'theme-exports'

const CustomTooltip = ({
    active,
    payload,
    tooltipPercent,
}: {
    tooltipPercent: boolean
    [key: string]: any
}) => {

    if (active && payload?.length) {
        return (
            <div
                className={'emp-pieChart-tooltipContainer'}
                style={{ fontSize: clientTheme.typography.body1.fontSize }}
            >
                <table style={{ borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr>
                            <td
                                className={'emp-pieChart-tooltipTableRow '}
                                style={{
                                    borderRight: '1px dotted #b3b3b3',
                                    textAlign: 'center',
                                }}
                            >
                                <FontAwesomeIcon
                                    size="xs"
                                    icon={faSquare}
                                    style={{
                                        marginRight: '5px',
                                        color: payload[0].payload.fill,
                                    }}
                                />
                                {payload[0]?.payload?.tooltipName
                                    ? payload[0]?.payload?.tooltipName
                                    : payload[0].name}
                            </td>
                            <td
                                className={'emp-pieChart-tooltipTableRow '}
                                style={{
                                    textAlign: 'right',
                                }}
                            >
                                {tooltipPercent
                                    ? `${payload[0].value.toFixed(2)}%`
                                    : Number(payload[0].value).toLocaleString(
                                          'en-US'
                                      )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        )
    }
    return null
}

//Needed to align piecharts on Reports page
const CustomLegend = () => {
    return <div style={{ height: 19 }}></div>
}

/**
 * @name PieChart
 */
export const PieChart = ({
    data = [],
    loading = false,
    showLegend = true,
    colorPalette,
    // width = 550,
    height = 380,
    // showValueOnHover = false,
    testId,
    tooltipPercent = false,
    pieLabelLayout = 'outside',
}: {
    data: any[]
    loading?: boolean
    showLegend?: boolean
    colorPalette?: string[]
    height?: number | string
    width?: number | string
    showValueOnHover?: boolean
    testId?: string
    tooltipPercent?: boolean
    pieLabelLayout?: 'inside' | 'outside'
}): React.ReactElement => {

    const [maxIndex, setMaxIndex] = useState<number>(0)
    const [activeIndex, setActiveIndex] = useState<number>(0)
    const updatedColorPalette =
        colorPalette ?? clientTheme.charts.pieCharts.defaultPieChart

    // on mouse enter for pie chart mouseover
    const onMouseEnter = (_: any, index: number) => setActiveIndex(index)
    const onMouseExit = () => setActiveIndex(maxIndex)

    const chartContainerEL = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState(0)

    useEffect(()=>{
        // Use to set the highest value as the initial/default active index.
        const maxIdx = data.reduce((maxIdx, item, idx, arr) => 
            Number(item.value) > Number(arr[maxIdx].value) ? idx : maxIdx, 0
        )
        setMaxIndex(maxIdx)
        setActiveIndex(maxIdx)
    },[data])

    useEffect(() => {
        const chartElement = chartContainerEL.current
        if (!chartElement) return

        const resizeObserver = new ResizeObserver(() => {
            setContainerWidth(chartElement.offsetWidth)
        })

        resizeObserver.observe(chartElement)

        setContainerWidth(chartElement.offsetWidth)

        return () => resizeObserver.disconnect()
    }, [])

    //custom active pie label shape
    const renderLabel = (props: any) => {
        const RADIAN = Math.PI / 180
        const { cx, cy, midAngle, outerRadius, fill, percent } = props
        const sin = Math.sin(-RADIAN * midAngle)
        const cos = Math.cos(-RADIAN * midAngle)
        const sx = cx + outerRadius * cos
        const sy = cy + outerRadius * sin
        const mx = cx + (outerRadius + 15) * cos
        const my = cy + (outerRadius + 15) * sin
        const ex = mx + (cos >= 0 ? 1 : -1) * 10
        const ey = my + (sin >= 0 ? +2 : -2)
        const textAnchor = cos >= 0 ? 'start' : 'end'

        return percent * 100 >= 1 ? (
            <g>
                <path
                    d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
                    stroke={fill}
                    fill="none"
                />
                <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
                <text
                    x={ex + (cos >= 0 ? 1 : -1) * 3}
                    y={ey + (sin >= 0 ? +4 : +2)}
                    textAnchor={textAnchor}
                    fill="grey"
                    fontSize="11px"
                    transform={`rotate(${
                        ((cos >= 0 && sin >= 0) || (cos <= 0 && sin <= 0)
                            ? 1
                            : -1) * 12
                    }, ${ex}, ${ey})`}
                >
                    {`${(percent * 100).toFixed(2)}%`}
                </text>
            </g>
        ) : null
    }

    const renderActiveShapeInside = (props: any) => {
        const isSmallPieChart = containerWidth < 230
        const {
            cx,
            cy,
            innerRadius,
            outerRadius,
            startAngle,
            endAngle,
            fill,
            payload,
            percent,
        } = props
        return (
            <g>
                <text
                    x={cx}
                    y={cy + 10}
                    dy={-7}
                    textAnchor="middle"
                    fill="#333333"
                    fontSize={isSmallPieChart ? '45px' : '55px'}
                >
                    {`${(percent * 100).toFixed(0)}%`}
                </text>
                <text
                    x={cx}
                    y={cy + 10}
                    dy={18}
                    textAnchor="middle"
                    fill="#333333"
                    fontSize={isSmallPieChart ? '16px' : '18px'}
                >
                    {payload.name.split(':')[0]}
                </text>
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius + 3}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
            </g>
        )
    }

    const renderActiveShapeOutside = (props: any) => {
        const {
            cx,
            cy,
            innerRadius,
            outerRadius,
            startAngle,
            endAngle,
            fill,
        } = props
        return (
            <g>
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius + 3}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
            </g>
        )
    }

    const getPieChartRadius = (startRadius: number) => {
        if (containerWidth < 210) {
            return startRadius - 45
        }
        if (containerWidth < 230) {
            return startRadius - 40
        }
        if (containerWidth < 245) {
            return startRadius - 30
        }
        if (containerWidth < 270) {
            return startRadius - 20
        }
        if (containerWidth < 285) {
            return startRadius - 10
        }
        return startRadius
    }

    return (
        <div
            ref={chartContainerEL}
            id={`${idDirectory.divContainer}-${testId}`}
            className={`emp-pieChart-root`}
        >
            {!loading ? (
                <>
                    {data.length ? (
                        <div
                            className={'emp-pieChart-pieChartContainer'}
                            id={`${idDirectory.divChart}-${testId}`}
                        >
                            <ResponsiveContainer width="100%" height={height}>
                                <RechartPieChart>
                                    <Pie
                                        data={data}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={
                                            pieLabelLayout === 'inside'
                                                ? getPieChartRadius(115)
                                                : 80
                                        }
                                        outerRadius={
                                            pieLabelLayout === 'inside'
                                                ? getPieChartRadius(140)
                                                : 130
                                        }
                                        paddingAngle={0.5}
                                        dataKey="value"
                                        labelLine={false}
                                        label={
                                            pieLabelLayout === 'inside'
                                                ? undefined
                                                : renderLabel
                                        }
                                        activeIndex={activeIndex}
                                        activeShape={
                                            pieLabelLayout === 'inside'
                                                ? renderActiveShapeInside
                                                : renderActiveShapeOutside
                                        }
                                        onMouseEnter={onMouseEnter}
                                        onMouseLeave={onMouseExit}
                                        isAnimationActive={false}
                                    >
                                        {data.map((entry, index) => {
                                            const name = entry.name
                                                .trim()
                                                .toLowerCase()
                                            return (
                                                <Cell
                                                    key={`cell-${index}-${entry.name}`}
                                                    fill={
                                                        [
                                                            'other',
                                                            'pending',
                                                        ].some((word) =>
                                                            name.startsWith(
                                                                word
                                                            )
                                                        )
                                                            ? 'grey'
                                                            : updatedColorPalette[
                                                                  index %
                                                                      updatedColorPalette.length
                                                              ]
                                                    }
                                                />
                                            )
                                        })}
                                    </Pie>
                                    <Tooltip
                                        content={
                                            <CustomTooltip
                                                tooltipPercent={tooltipPercent}
                                            />
                                        }
                                    />
                                    {showLegend ? (
                                        <Legend
                                            verticalAlign="bottom"
                                            align="center"
                                            wrapperStyle={{
                                                fontSize:
                                                    clientTheme.typography.body1
                                                        .fontSize,
                                            }}
                                        />
                                    ) : (
                                        <Legend content={<CustomLegend />} />
                                    )}
                                </RechartPieChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div
                            className={'emp-pieChart-noDataMessaging'}
                            id={`${idDirectory.divNoData}-${testId}`}
                        >
                            No Data
                        </div>
                    )}
                </>
            ) : (
                <div
                    className={'emp-pieChart-loadingContainer'}
                    id={`${idDirectory.divLoading}-${testId}`}
                >
                    <LoadingIndicator />
                </div>
            )}
        </div>
    )
}

export default PieChart
